/**
 * Stripe Events Log — superadmin red panel (Phase 3).
 */

import { useCallback, useEffect, useState } from 'react'
import { superadminAPI } from '../../services/api'
import {
  HiOutlineCreditCard,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineArrowPath,
} from 'react-icons/hi2'

export default function StripeEventsPage() {
  const [data, setData] = useState({ items: [], total: 0 })
  const [loading, setLoading] = useState(true)
  const [params, setParams] = useState({ event_type: '', processed: '', limit: 50, offset: 0 })

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const res = await superadminAPI.listStripeEvents(params)
      setData(res?.data?.data ?? res?.data ?? { items: [], total: 0 })
    } finally {
      setLoading(false)
    }
  }, [params])

  useEffect(() => { reload() }, [reload])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineCreditCard className="w-6 h-6 text-danger" /> Stripe Events
          </h1>
          <p className="text-gray-600 text-sm mt-1">Webhook events received from Stripe. Useful for debugging.</p>
        </div>
        <button onClick={reload} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
          <HiOutlineArrowPath className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600 flex items-center gap-2">
          Type:
          <input value={params.event_type} onChange={(e) => setParams(p => ({ ...p, event_type: e.target.value, offset: 0 }))}
            placeholder="customer.subscription.created"
            className="border border-gray-300 rounded px-2 py-1 text-sm w-72" />
        </label>
        <label className="text-sm text-gray-600 flex items-center gap-2">
          Processed:
          <select value={params.processed} onChange={(e) => setParams(p => ({ ...p, processed: e.target.value, offset: 0 }))}
            className="border border-gray-300 rounded px-2 py-1 text-sm">
            <option value="">Any</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </label>
        <span className="text-sm text-gray-500 ml-auto">{data.total} total</span>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-gray-700 text-xs uppercase tracking-wide">
              <th className="px-4 py-3">Received</th>
              <th className="px-4 py-3">Event type</th>
              <th className="px-4 py-3">Stripe id</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Subscription</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.items.map(e => (
              <tr key={e.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-600">{new Date(e.received_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-900 font-medium">{e.event_type}</td>
                <td className="px-4 py-3 text-xs"><code className="bg-gray-100 px-1 rounded">{e.stripe_event_id.slice(0, 20)}…</code></td>
                <td className="px-4 py-3">
                  {e.processed ? (
                    <span className="inline-flex items-center gap-1 text-success text-xs">
                      <HiOutlineCheckCircle className="w-4 h-4" /> processed
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-warning text-xs">
                      <HiOutlineExclamationCircle className="w-4 h-4" /> pending
                    </span>
                  )}
                  {e.processing_error && (
                    <div className="text-xs text-danger mt-0.5 max-w-md truncate" title={e.processing_error}>
                      Error: {e.processing_error.slice(0, 80)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {e.subscription_id ? `#${e.subscription_id}` : '—'}
                </td>
              </tr>
            ))}
            {data.items.length === 0 && !loading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">No Stripe events yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
